/**
 * The tester card, as the Profile screen sums it up (platform#56, over
 * RidgeWill/auth#645).
 *
 * ── THE CARD IS MADE ON RIDGEWILL.COM, AND ONLY SUMMED UP HERE ──────
 *
 * The form that builds it (apps, devices one at a time, the questions) lives on
 * the site at `/testers`, because that is where somebody signs up to test and
 * where adding a device by scanning a code works. The portal shows a short
 * summary and links back to the form to change it. Two editors for one card
 * would drift, and the one here would be the one nobody tests.
 *
 * The one thing the portal does itself is delete the card, because "stop" is
 * an account decision and this is where people come to make those.
 *
 * ── WHY THIS IS A MODULE AND NOT JSX ────────────────────────────────
 *
 * The same reason as lib/subscriptions: the status line is a decision about
 * dates and states, and the display words are a table that has to agree with
 * the site's. Pure functions can be tested against each.
 */
import { SITE_URL } from './site.js';
import { formatDay } from './subscriptions.js';

/** Where the card is made and changed. The dev site comes in through SITE_URL. */
export const TESTER_FORM_URL = `${SITE_URL}/testers`;

/** Any stage: the form offers what staff switch on, not a set of stages (auth#647). */
export type AppStatus = 'planning' | 'in-dev' | 'alpha' | 'beta' | 'live';
export type DeviceType = 'phone' | 'tablet' | 'computer';
export type DeviceOs = 'ios' | 'ipados' | 'android' | 'windows' | 'macos' | 'chromeos' | 'linux';

export type Experience = 'regularly' | 'some' | 'never';
export type Feedback = 'detailed' | 'short' | 'unsure';
export type TimeGiven = '15m' | '1h' | 'more';

/**
 * The answers, as auth stores them: KEYS, not sentences.
 *
 * The site renders the same keys with the same words (the table below), so a
 * reworded option changes in two places on purpose rather than leaving old
 * copy frozen into every card that was made before the change.
 *
 * Every field is optional: a card is saved as it is filled in, and a card not
 * sent yet has gaps.
 */
export interface TesterAnswers {
  experience?: Experience;
  feedback?: Feedback;
  time?: TimeGiven;
  inviteEmail?: string;
  accessibility?: string[];
  agreeChanges?: boolean;
  agreePrivate?: boolean;
}

export interface TesterDevice {
  id: string;
  type: DeviceType;
  os: DeviceOs;
  /** Free text from the form. Any of these can be empty when it was skipped. */
  version?: string | null;
  model?: string | null;
  storage?: string | null;
  memory?: string | null;
  browser?: string | null;
  source: 'this' | 'scan' | 'hand';
  createdAt: string;
}

export interface TesterCard {
  id: string;
  /** App slugs, or `['all']` for every app taking testers, now and later. */
  apps: string[];
  answers: TesterAnswers;
  /** Null until the first Send. Sending again after an edit moves it. */
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  devices: TesterDevice[];
}

/** An app taking testers right now, as auth lists it beside the card. */
export interface TestableApp {
  slug: string;
  name: string;
  description: string;
  status: AppStatus;
  needs: string;
}

/**
 * What still stands between the card and Send, most important first.
 *
 * The SERVER decides, and writes the sentence. The rules (which app needs
 * which device, which questions are required) live with the form, and a
 * recount here from missing answers would agree with them only until somebody
 * made a question optional.
 */
export interface TesterBlocker {
  code: string;
  message: string;
  app?: string;
}

export interface TesterCardResponse {
  card: TesterCard | null;
  apps: TestableApp[];
  blockers: TesterBlocker[];
}

// ── Display words. The same table as the site's; see the auth contract. ──

export const STATUS_LABEL: Record<AppStatus, string> = {
  planning: 'Planning',
  'in-dev': 'In development',
  alpha: 'Alpha',
  beta: 'Beta',
  live: 'Live',
};

export const OS_LABEL: Record<DeviceOs, string> = {
  ios: 'iOS',
  ipados: 'iPadOS',
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
  chromeos: 'ChromeOS',
  linux: 'Linux',
};

export const DEVICE_LABEL: Record<DeviceType, string> = {
  phone: 'Phone',
  tablet: 'Tablet',
  computer: 'Computer',
};

export const EXPERIENCE_LABEL: Record<Experience, string> = {
  regularly: 'Yes, regularly',
  some: 'A few times',
  never: 'Never, this would be my first time',
};

export const FEEDBACK_LABEL: Record<Feedback, string> = {
  detailed: 'Yes: steps, screenshots, what I expected',
  short: 'Short notes when something breaks',
  unsure: 'Not sure yet',
};

export const TIME_LABEL: Record<TimeGiven, string> = {
  '15m': 'About 15 minutes a week',
  '1h': 'About an hour a week',
  more: 'A few hours a week',
};

/**
 * A stored key in words, or null when there is no answer.
 *
 * A key this build does not know (a newer option on the site) also comes back
 * null rather than as the raw key: "Not answered yet" is wrong for a moment,
 * whereas `2h` on screen looks like a bug forever.
 */
export function answerLabel<K extends string>(table: Record<K, string>, key: string | undefined): string | null {
  if (!key) return null;
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key as K] : null;
}

/**
 * The apps on the card, resolved to the ones taking testers now.
 *
 * `all` is every app on the list. A slug that is no longer taking testers
 * drops out, which is what auth does when it works out the blockers, so the
 * summary and the "still needed" list never disagree about what is on the card.
 */
export function cardApps(card: TesterCard, apps: TestableApp[]): TestableApp[] {
  if (card.apps.includes('all')) return apps;
  return apps.filter((a) => card.apps.includes(a.slug));
}

/** "Pixel 9" when they named it, the kind of device when they did not. */
export function deviceName(device: TesterDevice): string {
  return device.model?.trim() || DEVICE_LABEL[device.type] || 'Device';
}

/** "Android 16", or just "Android" when the version was skipped. */
export function deviceSystem(device: TesterDevice): string {
  const os = OS_LABEL[device.os] ?? device.os;
  const version = device.version?.trim();
  return version ? `${os} ${version}` : os;
}

/** The local calendar day, so "today" means the reader's today. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Where the card stands, in one line: "Sent on October 18, 2026", "Sent
 * today", or "Not sent yet".
 *
 * "Not sent yet" is the first thing an unsent card says, because the most
 * likely misunderstanding is believing Ridgewill already has it: the form saves
 * as it goes, so everything typed is there, and none of it has been sent.
 *
 * `now` and `format` are injectable for the reason `describeSubscription`'s
 * are: the copy is what gets tested, and the clock and the locale are not.
 */
export function cardStatus(
  card: TesterCard,
  now: Date = new Date(),
  format: (iso: string) => string = formatDay,
): { text: string; sent: boolean } {
  if (!card.submittedAt) return { text: 'Not sent yet', sent: false };
  const then = new Date(card.submittedAt);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(then) === dayKey(now)) return { text: 'Sent today', sent: true };
  if (dayKey(then) === dayKey(yesterday)) return { text: 'Sent yesterday', sent: true };
  return { text: `Sent on ${format(card.submittedAt)}`, sent: true };
}
